import { useState } from "react";
import { AlertOctagon, Copy, RefreshCw, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Iter390 — Owner backup-clone controls. Renders as a row of buttons on
// each AdminFestivals card. Handles create / switch / delete with a
// non-dismissible confirmation for the switch action.
export default function CloneControls({ festival, onChanged }) {
  const [confirm, setConfirm] = useState(null); // 'switch' | 'delete' | null
  const [busy, setBusy] = useState(false);

  const hasClone = !!festival.clone_group_id && !festival.is_clone;
  const activated = hasClone && !festival.is_active_copy;

  const doCreate = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/festival/${festival.id}/clone`);
      toast.success("Backup clone created — paused and hidden");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };
  const doSwitch = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/festival/${festival.id}/clone/switch`, { confirm: true });
      toast.success("Switched — organiser + deputies notified");
      setConfirm(null);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };
  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/festival/${festival.id}/clone`);
      toast.success("Backup clone deleted");
      setConfirm(null);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      {!hasClone && (
        <button type="button" onClick={(e) => { e.stopPropagation(); doCreate(); }}
          disabled={busy} data-testid={`admin-clone-create-${festival.id}`}
          title="Create paused backup copy — hidden from organiser + players"
          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black disabled:opacity-40">
          <Copy className="w-3 h-3" /> Create backup
        </button>
      )}
      {hasClone && (
        <>
          <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-[#FFB020] text-[#FFB020]"
                data-testid={`admin-clone-badge-${festival.id}`}>
            Backup exists · Active: {festival.is_active_copy ? "original" : "backup"}
          </span>
          <button type="button" onClick={(e) => { e.stopPropagation(); setConfirm("switch"); }}
            disabled={busy} data-testid={`admin-clone-switch-${festival.id}`}
            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest border border-[#FFB020] text-[#FFB020] hover:bg-[#FFB020] hover:text-black disabled:opacity-40">
            <RefreshCw className="w-3 h-3" /> Switch to {festival.is_active_copy ? "backup" : "original"}
          </button>
          {!activated && (
            <button type="button" onClick={(e) => { e.stopPropagation(); setConfirm("delete"); }}
              disabled={busy} data-testid={`admin-clone-delete-${festival.id}`}
              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black disabled:opacity-40">
              <Trash2 className="w-3 h-3" /> Delete backup
            </button>
          )}
        </>
      )}
      {confirm === "switch" && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" data-testid="admin-clone-switch-modal">
          <div className="w-full max-w-md bg-[#0A0A0A] border-2 border-[#FFB020] p-5">
            <div className="flex items-start gap-3 mb-3">
              <AlertOctagon className="w-6 h-6 text-[#FFB020] shrink-0 mt-0.5" />
              <div>
                <div className="label-mono text-[#FFB020] text-[10px]">RALLY-LAB · SWITCH BACKUP</div>
                <h2 className="h-display text-xl mt-1">Switch active copy</h2>
              </div>
            </div>
            <p className="text-sm text-white mb-4">
              You are switching <b>{festival.name}</b> to the backup copy. Players will be redirected automatically. The previous copy will be paused. Confirm?
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setConfirm(null)} disabled={busy}
                data-testid="admin-clone-switch-cancel"
                className="px-4 py-2 text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-white disabled:opacity-40">Cancel</button>
              <button type="button" onClick={doSwitch} disabled={busy}
                data-testid="admin-clone-switch-confirm"
                className="px-4 py-2 text-xs uppercase tracking-widest bg-[#FFB020] text-black hover:opacity-80 disabled:opacity-40">
                {busy ? "Switching…" : "Confirm & switch"}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirm === "delete" && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" data-testid="admin-clone-delete-modal">
          <div className="w-full max-w-md bg-[#0A0A0A] border-2 border-[#FF4500] p-5">
            <div className="flex items-start gap-3 mb-3">
              <Trash2 className="w-6 h-6 text-[#FF4500] shrink-0 mt-0.5" />
              <div>
                <div className="label-mono text-[#FF4500] text-[10px]">RALLY-LAB · DELETE BACKUP</div>
                <h2 className="h-display text-xl mt-1">Delete backup clone</h2>
              </div>
            </div>
            <p className="text-sm text-white mb-4">
              Delete the paused backup for <b>{festival.name}</b>? This cannot be undone. Only possible before the backup is activated.
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setConfirm(null)} disabled={busy}
                data-testid="admin-clone-delete-cancel"
                className="px-4 py-2 text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-white disabled:opacity-40">Cancel</button>
              <button type="button" onClick={doDelete} disabled={busy}
                data-testid="admin-clone-delete-confirm"
                className="px-4 py-2 text-xs uppercase tracking-widest bg-[#FF4500] text-black hover:opacity-80 disabled:opacity-40">
                {busy ? "Deleting…" : "Confirm delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
